/* =============================================================================
   VIEWS / ADMIN / OPERATOR-UI — Operator-Layer Frontend (S125, Phase B)
   =============================================================================
   UI über dem Operator-State (core/operator-session.js):
     - Geräte-Onboarding (erster Start ohne registriertes Gerät).
     - Operator-Kachel auf dem Dashboard (ersetzt die Warteliste-Kachel).
     - Wechsel-Modal: Namensliste → PIN-Pad.
     - Idle-/Sperr-Overlay (kein View-Hijack — Overlay über der laufenden View,
       View läuft darunter live weiter, Memory feedback_gg_no_view_hijack).

   Phase B blockt KEINE Schreibaktionen (strict-Enforcement kommt server-seitig
   in Phase C). Die Kachel zeigt aber „Kein Operator angemeldet" als Warnzustand.

   CSP: alle Inline-Handler als `func()` ohne window. (Memory
   feedback_gg_inline_handler_csp) — dom-bindings.js bindet sie via
   MutationObserver. Non-destruktive Renders: Operator-Wechsel aktualisiert nur
   die Kachel (#dash-operator-card), kein Tab-Full-Rerender.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { closeModal } from '../../utils/dom.js';
import {
  getDeviceId, getOperator, isOperatorActive, isDeviceRegistered,
  hydrateDevice, hydrateOperator, operatorLogin, operatorLogout, registerDevice,
  subscribeOperatorRealtime, operatorErrorMessage
} from '../../core/operator-session.js';
import { ROLE_LABELS, roleIdleTimeoutMinutes } from '../../core/operator-roles.js';
import { markMainActivity } from '../../core/session-idle.js';

// Modul-State
let _initialized = false;
let _teamUsers = [];          // aktive Team-User (für Namensliste)
let _teamLoaded = false;
let _authContext = null;      // 'switch' | 'lock' | null — wohin gerendert wird
let _selectedUserId = null;
let _selectedUserName = '';
let _pinBuffer = '';
let _authMessage = '';
let _busy = false;

// Idle-Watch
let _lastActivity = 0;
let _idleInterval = null;
let _activityBound = false;
let _locked = false;
// S222-HF1 — zuletzt gesehene Operator-Session (Identitäts-Tracking über
// operator:changed). Wechselt sie (neuer Login/Wechsel, auch null→op), startet
// das Idle-Budget des neuen Operators frisch — in JEDEM Tab.
let _lastSeenOperatorSessionId = null;

const PIN_LENGTH = 4;
const IDLE_CHECK_MS = 15000;

/* -------------------------------------------------------------------------- */
/* Init                                                                        */
/* -------------------------------------------------------------------------- */

/** Idempotenter Boot des Operator-Layers — aus initAdminShell aufgerufen. */
export async function ensureOperatorLayer() {
  if (_initialized) return;
  _initialized = true;

  const App = typeof window !== 'undefined' && window.App;
  if (App && App.events && typeof App.events.on === 'function') {
    App.events.on('operator:changed', _onOperatorChanged);
  }

  try {
    await hydrateDevice();
    if (!isDeviceRegistered()) {
      openDeviceOnboarding();
    } else {
      await hydrateOperator();
      subscribeOperatorRealtime();
      // S222 Cut 1 (L1, Teil 2) — Re-Route-Sicherheitsnetz nach der Hydration.
      _reRouteDriverIfNeeded();
    }
  } catch (e) {
    console.warn('[operator-ui] init:', e && e.message);
  }
  _bindActivityListeners();
  _syncIdleWatch();
  refreshOperatorCardDom();
}

/** S222 Cut 1 (L1) — lief das Fahrer-Gate (view-switch) beim Boot zu früh
 *  (Operator-State noch null → keine Umleitung), landet ein reiner Fahrer im
 *  Cockpit. Nach der Hydration ist die Rolle bekannt: Primär-Rolle `driver` UND
 *  Admin-View aktiv → aufs Terminal umleiten. NUR admin→driver (kein View-Hijack
 *  aus KDS/Bar/Delivery). Memory feedback_gg_no_view_hijack. */
function _reRouteDriverIfNeeded() {
  try {
    const op = getOperator();
    if (!op || op.role !== 'driver') return;
    const adminEl = document.getElementById('view-admin');
    const adminActive = adminEl && adminEl.style.display === 'block';
    if (adminActive && typeof window.switchView === 'function') {
      window.switchView('driver');
    }
  } catch (_) { /* ignore */ }
}

/** S222-HF1 — Reaktion auf jeden Operator-Wechsel (lokal per PIN ODER via Realtime
 *  aus einem anderen Tab/Fenster → subscribeOperatorRealtime → hydrateOperator →
 *  operator:changed). Wechselt die Operator-IDENTITÄT (sessionId), IMMER die
 *  Aktivität frisch stempeln — sonst zählt ein bereits laufender Idle-Timer die
 *  Inaktivität des VORIGEN Operators weiter gegen das Budget des NEUEN (Live-Befund:
 *  Owner mit 5-Min-Timeout wurde 61 s nach Anmeldung idle-abgemeldet, weil
 *  _lastActivity vom vorigen Operator stehen blieb). Bei op→null (Logout) NICHT
 *  stempeln. _markActivity VOR _syncIdleWatch, damit ein weiterlaufender Timer sofort
 *  das frische _lastActivity sieht. */
function _onOperatorChanged() {
  const op = getOperator();
  const sid = op ? op.sessionId : null;
  if (sid !== _lastSeenOperatorSessionId) {
    _lastSeenOperatorSessionId = sid;
    if (sid) _markActivity();
  }
  refreshOperatorCardDom();
  _syncIdleWatch();
}

/* -------------------------------------------------------------------------- */
/* Team-User-Liste                                                             */
/* -------------------------------------------------------------------------- */

async function loadTeamUsers(force) {
  if (_teamLoaded && !force) return _teamUsers;
  const supa = (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
  if (!supa) return [];
  try {
    const { data, error } = await supa.rpc('list_team_users');
    if (error) throw error;
    _teamUsers = (Array.isArray(data) ? data : []).filter(u => u && u.active);
    _teamLoaded = true;
  } catch (e) {
    console.warn('[operator-ui] loadTeamUsers:', e && e.message);
    _teamUsers = [];
  }
  return _teamUsers;
}

/* -------------------------------------------------------------------------- */
/* Operator-Kachel (Dashboard)                                                 */
/* -------------------------------------------------------------------------- */

/** Initialen-Fallback aus einem Namen (nur Anzeige, wenn keine vergeben sind).
 *  S126 Befund 4: nur BUCHSTABEN verwenden — Klammern/Ziffern/Bindestriche am
 *  Wortanfang überspringen (Umlaute bleiben). Erste Buchstaben der ersten beiden
 *  Wörter; Einzelwort → erste zwei Buchstaben. „Jonas (Manager-Test)" → JM,
 *  „Anna-Lena Müller" → AM, „Max" → MA. */
export function fallbackInitials(name) {
  const firstLetter = (w) => { const m = String(w).match(/\p{L}/u); return m ? m[0] : ''; };
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  const letters = words.map(firstLetter).filter(Boolean);
  if (letters.length >= 2) return (letters[0] + letters[1]).toUpperCase();
  if (letters.length === 1) {
    const w = words.find(x => firstLetter(x));
    const two = (String(w).match(/\p{L}/gu) || []).slice(0, 2).join('');
    return (two || letters[0]).toUpperCase();
  }
  const all = String(name || '').match(/\p{L}/gu);
  return all ? all.slice(0, 2).join('').toUpperCase() : '–';
}

/* -------------------------------------------------------------------------- */
/* S132/Phase E — „Wer ist aktiv"-Presence-Widget (nur manager/owner/admin)    */
/* -------------------------------------------------------------------------- */

let _activeOps = [];
let _activeOpsLoaded = false;
let _activeOpsChannel = null;
let _activeOpsDebounce = null;

/** Pure: kompakte deutsche Relativzeit („gerade eben" / „vor 3 Min" / „vor 2 Std"). */
export function relTimeShort(ts, nowMs) {
  const t = ts ? Date.parse(ts) : NaN;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  if (!Number.isFinite(t)) return '—';
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 45) return 'gerade eben';
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} Min`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} Std`;
  return `vor ${Math.round(std / 24)} T`;
}

/** Pure: ein Presence-Chip (Initialen) → HTML. title trägt das Detail
 *  „Name · Rolle · Gerät · aktiv vor X" ohne UI-Fläche. */
export function _activeOpChipHtml(row, nowMs) {
  const initials = (row.operator_initials || fallbackInitials(row.operator_name)) || '–';
  const name = row.operator_name || '—';
  const role = ROLE_LABELS[row.operator_role] || row.operator_role || '';
  const device = row.device_label || row.device_id || '—';
  const title = `${name}${role ? ' · ' + role : ''} · ${device} · aktiv ${relTimeShort(row.last_action_at || row.started_at, nowMs)}`;
  return `<span class="op-chip" title="${htmlEscape(title)}">${htmlEscape(initials)}</span>`;
}

/** Pure-ish: Chips-HTML aller aktiven Operatoren, eigenes Gerät zuerst. */
export function _activeOpsChipsHtml() {
  if (!_activeOpsLoaded || !_activeOps.length) return '';
  const ownDevice = getDeviceId();
  const now = Date.now();
  const sorted = _activeOps.slice().sort((a, b) =>
    (b.device_id === ownDevice ? 1 : 0) - (a.device_id === ownDevice ? 1 : 0));
  return sorted.map(r => _activeOpChipHtml(r, now)).join('');
}

/** Lädt die aktiven Operatoren (role-gated RPC) + füllt die Topbar-Chips. */
export async function refreshActiveOperators() {
  const supa = (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
  if (!supa) return;
  try {
    const { data, error } = await supa.rpc('list_active_operators');
    if (error) throw error;
    _activeOps = Array.isArray(data) ? data : [];
    _activeOpsLoaded = true;
  } catch (e) {
    // NICHT_BERECHTIGT o.ä. → still bleiben (Chips werden für die Rolle eh nicht gezeigt)
    _activeOpsLoaded = true;
    console.warn('[operator] refreshActiveOperators:', e && e.message);
  }
  const el = document.getElementById('active-operators-chips');
  if (el) el.innerHTML = _activeOpsChipsHtml();
}

/** Eine (1) guarded Realtime-Subscription auf operator_sessions des Restaurants
 *  → bei Login/Wechsel/Logout an JEDEM Gerät die Presence-Liste neu laden.
 *  Kein Channel-Wildwuchs (idempotent). */
function subscribeActiveOperatorsRealtime() {
  const supa = (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
  if (!supa || _activeOpsChannel) return;
  try {
    _activeOpsChannel = supa
      .channel('active-operators')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'operator_sessions' },
        // S132d — debounced: phase_98 stempelt last_action_at bei JEDEM Write →
        // operator_sessions feuert pro Aktion. Bursts (z.B. schneller Storno-/
        // Status-Klick-Folge) in EINEN Refresh zusammenfassen statt N RPCs.
        () => { _scheduleActiveOpsRefresh(); })
      .subscribe();
  } catch (e) {
    console.warn('[operator] presence realtime:', e && e.message);
  }
}

/** Trailing-Debounce (450 ms) für den Presence-Refresh. */
function _scheduleActiveOpsRefresh() {
  if (_activeOpsDebounce) clearTimeout(_activeOpsDebounce);
  _activeOpsDebounce = setTimeout(() => { _activeOpsDebounce = null; refreshActiveOperators(); }, 450);
}

/** Vom Dashboard nach dem Render aufgerufen (nur wenn die Karte sichtbar ist). */
export function ensureActiveOperatorsWidget() {
  refreshActiveOperators();
  subscribeActiveOperatorsRealtime();
}

/** HTML der Operator-Kachel — passt ins Dashboard-Widget-Grid. */
export function renderOperatorCard() {
  const op = getOperator();
  if (op) {
    const initials = op.initials || fallbackInitials(op.name);
    const role = ROLE_LABELS[op.role] || op.role || '';
    const fullName = op.name || 'Angemeldet';
    const titleText = `${fullName}${role ? ' · ' + role : ''} — tippen zum Wechseln`;
    return `
      <div class="widget is-clickable operator-card" id="dash-operator-card"
           onclick="operatorOpenSwitch()" tabindex="0" title="${htmlEscape(titleText)}">
        <div class="widget-head">
          <span class="widget-label">Operator</span>
          <span class="widget-icon" aria-hidden="true">👤</span>
        </div>
        <div class="operator-card-body">
          <span class="operator-badge">${htmlEscape(initials)}</span>
          <span class="operator-card-meta">
            <span class="operator-card-name" title="${htmlEscape(fullName)}">${htmlEscape(fullName)}</span>
            <span class="operator-card-role">${htmlEscape(role)}</span>
          </span>
        </div>
        <div class="widget-sub">Tippen zum Wechseln</div>
      </div>
    `;
  }
  return `
    <div class="widget is-clickable operator-card widget-accent operator-card-empty" id="dash-operator-card"
         onclick="operatorOpenSwitch()" tabindex="0" title="Operator anmelden">
      <div class="widget-head">
        <span class="widget-label">Operator</span>
        <span class="widget-icon" aria-hidden="true">👤</span>
      </div>
      <div class="widget-value">—</div>
      <div class="widget-sub">⚠ Kein Operator angemeldet · Tippen zum Anmelden</div>
    </div>
  `;
}

/** Badge-Initialen: vergebene Initialen bevorzugt, sonst Fallback aus dem Namen. */
export function operatorBadgeInitials(initials, name) {
  const i = (initials || '').trim();
  if (i) return i;
  return name ? fallbackInitials(name) : '';
}

/** Single-Source-Renderer für die Operator-Attribution-Badges auf einer Kachel
 *  (S127 Phase C). Ein Helper für alle Displays (Kanban/KDS/Theke). Zeigt
 *  „aufgenommen von" (✎) + „letzter Statuswechsel" (⟳, nur wenn abweichend).
 *  Liest die vom BEFORE-Trigger gestempelten Felder am Order/Reservierungs-Objekt. */
export function renderOperatorBadges(entity) {
  if (!entity) return '';
  const createdInit = operatorBadgeInitials(entity.createdByOperatorInitials, entity.createdByOperatorName);
  const lastInit = operatorBadgeInitials(entity.lastActionOperatorInitials, entity.lastActionOperatorName);
  const badges = [];
  if (createdInit) {
    badges.push(`<span class="op-badge op-badge-created" title="Aufgenommen von ${htmlEscape(entity.createdByOperatorName || '')}">✎ ${htmlEscape(createdInit)}</span>`);
  }
  // „letzte Aktion" nur zeigen, wenn ein ANDERER Operator zuletzt gehandelt hat.
  if (lastInit && entity.lastActionOperator && entity.lastActionOperator !== entity.createdByOperator) {
    badges.push(`<span class="op-badge op-badge-action" title="Letzter Statuswechsel: ${htmlEscape(entity.lastActionOperatorName || '')}">⟳ ${htmlEscape(lastInit)}</span>`);
  }
  return badges.length ? `<span class="op-badges">${badges.join('')}</span>` : '';
}

/** Non-destruktives Update NUR der Operator-Kachel. */
export function refreshOperatorCardDom() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('dash-operator-card');
  if (!el) return; // Dashboard nicht aktiv — nächster renderDashboard zieht den State
  el.outerHTML = renderOperatorCard();
}

/* -------------------------------------------------------------------------- */
/* Wechsel-Modal + Sperr-Overlay (geteilte Auth-UI)                            */
/* -------------------------------------------------------------------------- */

/** Öffnet das Wechsel-Modal (dismissbar) — Namensliste → PIN. */
export async function openOperatorSwitch() {
  if (!isDeviceRegistered()) { openDeviceOnboarding(); return; }
  _authContext = 'switch';
  _resetAuthInput();
  _renderAuthInto('switch');
  await loadTeamUsers();
  if (_authContext === 'switch') _renderAuthInto('switch');
}

/** Zeigt das Sperr-Overlay (NICHT dismissbar) bei Idle-Timeout. */
async function showLockOverlay() {
  _authContext = 'lock';
  _resetAuthInput();
  _ensureLockOverlayEl();
  _renderAuthInto('lock');
  await loadTeamUsers();
  if (_authContext === 'lock') _renderAuthInto('lock');
}

function hideLockOverlay() {
  const el = document.getElementById('operator-lock-overlay');
  if (el) el.classList.remove('active');
  _locked = false;
}

function _ensureLockOverlayEl() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('operator-lock-overlay');
  if (!el) {
    el = document.createElement('div');
    el.id = 'operator-lock-overlay';
    el.className = 'operator-lock-overlay';
    document.body.appendChild(el);
  }
  el.classList.add('active');
  return el;
}

function _resetAuthInput() {
  _selectedUserId = null;
  _selectedUserName = '';
  _pinBuffer = '';
  _authMessage = '';
  _busy = false;
}

/** Rendert die geteilte Auth-UI (Namensliste ODER PIN-Pad) in das Ziel des
 *  aktuellen Kontexts (Modal bei 'switch', Lock-Overlay bei 'lock'). */
function _renderAuthInto(ctx) {
  const html = _renderAuthHtml(ctx);
  if (ctx === 'lock') {
    const el = _ensureLockOverlayEl();
    if (el) el.innerHTML = `<div class="operator-auth-panel">${html}</div>`;
    return;
  }
  // switch → Standard-Modal
  const content = document.getElementById('modal-content');
  const overlay = document.getElementById('modal-overlay');
  if (content) content.innerHTML = html;
  if (overlay) overlay.classList.add('active');
}

function _renderAuthHtml(ctx) {
  const isLock = ctx === 'lock';
  const title = isLock ? '🔒 Gerät gesperrt' : 'Operator wechseln';
  const closeBtn = isLock
    ? ''
    : `<button class="modal-close" onclick="closeModal()" aria-label="Schließen">×</button>`;
  const sub = isLock
    ? 'Inaktiv — bitte erneut anmelden. Die Anzeige läuft im Hintergrund weiter.'
    : 'Wer übernimmt das Gerät? Name antippen und PIN eingeben.';

  let inner;
  if (!_selectedUserId) {
    inner = _renderUserListHtml();
  } else {
    inner = _renderPinPadHtml();
  }
  const msg = _authMessage
    ? `<p class="operator-auth-msg" role="alert">${htmlEscape(_authMessage)}</p>`
    : '';
  return `
    <div class="modal-header">
      <h2 style="margin:0;">${title}</h2>
      ${closeBtn}
    </div>
    <p class="hint" style="margin:.2rem 0 1rem;">${sub}</p>
    ${msg}
    ${inner}
  `;
}

function _renderUserListHtml() {
  if (!_teamLoaded) {
    return `<p class="hint">Lade Team-Liste…</p>`;
  }
  if (!_teamUsers.length) {
    return `<p class="hint">Keine aktiven Mitarbeiter gefunden. Bitte im Team-Tab anlegen.</p>`;
  }
  const tiles = _teamUsers.map(u => {
    const initials = u.display_initials || fallbackInitials(u.name);
    const role = ROLE_LABELS[u.role] || u.role || '';
    const noPin = (u.has_pin === false)
      ? `<span class="operator-tile-nopin" title="Noch keine PIN vergeben">PIN fehlt</span>`
      : '';
    return `
      <button type="button" class="operator-user-tile" onclick="operatorPickUser('${htmlEscape(u.id)}')">
        <span class="operator-badge">${htmlEscape(initials)}</span>
        <span class="operator-tile-name">${htmlEscape(u.name || '—')}</span>
        <span class="operator-tile-role">${htmlEscape(role)}</span>
        ${noPin}
      </button>
    `;
  }).join('');
  const current = isOperatorActive()
    ? `<button type="button" class="btn btn-ghost btn-sm operator-logout-btn" onclick="operatorLogoutNow()">Aktuellen Operator abmelden</button>`
    : '';
  return `<div class="operator-user-grid">${tiles}</div>${current}`;
}

/** Geteilte PIN-Pad-Markup (Login-Wechsel UND Team-PIN-Modal nutzen sie —
 *  keine Duplikation). Handler-Namen werden als Strings übergeben (CSP: func()). */
function _pinKeypadHtml({ buffer, whoHtml, keyFn, clearFn, delFn, backFn, backLabel }) {
  const dots = Array.from({ length: PIN_LENGTH }, (_, i) =>
    `<span class="operator-pin-dot ${i < (buffer || '').length ? 'filled' : ''}"></span>`).join('');
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d =>
    `<button type="button" class="operator-pin-key" onclick="${keyFn}('${d}')">${d}</button>`).join('');
  const back = backFn
    ? `<button type="button" class="btn btn-ghost btn-sm operator-pin-back" onclick="${backFn}()">${backLabel || '←'}</button>`
    : '';
  return `
    <div class="operator-pin-wrap">
      ${back}
      ${whoHtml ? `<div class="operator-pin-who">${whoHtml}</div>` : ''}
      <div class="operator-pin-dots">${dots}</div>
      <div class="operator-pin-pad">
        ${keys}
        <button type="button" class="operator-pin-key operator-pin-clear" onclick="${clearFn}()">C</button>
        <button type="button" class="operator-pin-key" onclick="${keyFn}('0')">0</button>
        <button type="button" class="operator-pin-key operator-pin-del" onclick="${delFn}()">⌫</button>
      </div>
    </div>
  `;
}

function _renderPinPadHtml() {
  return _pinKeypadHtml({
    buffer: _pinBuffer,
    whoHtml: `PIN für <strong>${htmlEscape(_selectedUserName)}</strong>`,
    keyFn: 'operatorPinInput', clearFn: 'operatorPinClear', delFn: 'operatorPinDel',
    backFn: 'operatorPinBack', backLabel: '← Name'
  });
}

function _rerenderAuth() {
  if (_authContext) _renderAuthInto(_authContext);
}

/* ---- Handler (window-exposed) ---- */

export function operatorPickUser(userId) {
  const u = _teamUsers.find(x => x.id === userId);
  _selectedUserId = userId;
  _selectedUserName = (u && u.name) || '';
  _pinBuffer = '';
  _authMessage = (u && u.has_pin === false)
    ? 'Für diesen Mitarbeiter ist noch keine PIN gesetzt (Team-Einstellungen).'
    : '';
  _rerenderAuth();
}

export function operatorPinBack() {
  _selectedUserId = null;
  _selectedUserName = '';
  _pinBuffer = '';
  _authMessage = '';
  _rerenderAuth();
}

export function operatorPinClear() {
  _pinBuffer = '';
  _rerenderAuth();
}

export function operatorPinDel() {
  _pinBuffer = _pinBuffer.slice(0, -1);
  _rerenderAuth();
}

export function operatorPinInput(digit) {
  if (_busy) return;
  if (!/^[0-9]$/.test(String(digit))) return;
  if (_pinBuffer.length >= PIN_LENGTH) return;
  _pinBuffer += String(digit);
  _rerenderAuth();
  if (_pinBuffer.length === PIN_LENGTH) {
    _submitPin();
  }
}

async function _submitPin() {
  if (_busy || !_selectedUserId) return;
  _busy = true;
  _authMessage = 'Prüfe PIN…';
  _rerenderAuth();
  const pin = _pinBuffer;
  const res = await operatorLogin(_selectedUserId, pin);
  _busy = false;
  if (res && res.ok) {
    const name = res.operator_name || _selectedUserName;
    if (_authContext === 'lock') hideLockOverlay();
    else closeModal();
    _authContext = null;
    _resetAuthInput();
    _markActivity();
    _syncIdleWatch();
    showToast(`Angemeldet als ${name}`, 'success');
    // S209 — Auto-Einstieg ins Fahrer-Terminal: die Primär-Rolle 'driver' meint
    // einen Fahrer-first-Operator (Multi-Role manager+driver hat Primär 'manager'
    // → bleibt im Cockpit, kein Trap). Der Operator-Chip im Terminal bleibt
    // erreichbar (Wechsel/Abmelden jederzeit).
    if (res.operator_role === 'driver' && typeof window.switchView === 'function') {
      window.switchView('driver');
    }
    return;
  }
  // Fehler: Meldung zeigen, PIN-Buffer leeren, beim Namen bleiben.
  _pinBuffer = '';
  _authMessage = operatorErrorMessage(res || {}, Date.now());
  // Bei Sperre / keiner PIN zurück zur Namensliste (PIN-Eingabe sinnlos).
  if (res && (res.error === 'PIN_GESPERRT' || res.error === 'KEINE_PIN')) {
    _selectedUserId = null;
    _selectedUserName = '';
  }
  _rerenderAuth();
}

export async function operatorLogoutNow() {
  await operatorLogout('logout');
  showToast('Operator abgemeldet.', 'info');
  closeModal();
  _authContext = null;
  _resetAuthInput();
  _syncIdleWatch();
}

/* -------------------------------------------------------------------------- */
/* Generisches PIN-Modal (S126 Befund 3 — ersetzt window.prompt im Team-Tab)   */
/* -------------------------------------------------------------------------- */

let _padBuffer = '';
let _padTitle = '';
let _padSubtitleHtml = '';
let _padMsg = '';
let _padBusy = false;
let _padOnComplete = null;

/** Öffnet ein PIN-Pad-Modal (4 Ziffern, Auto-Submit). `onComplete(pin)` muss
 *  ein {ok:true} | {ok:false,message} (auch Promise) liefern; bei ok schließt
 *  das Modal, sonst Meldung + Buffer leeren. Stylebar, kein window.prompt. */
export function openSetPinModal({ title, subtitle, onComplete } = {}) {
  _padBuffer = '';
  _padTitle = title || 'PIN festlegen';
  _padSubtitleHtml = subtitle ? htmlEscape(subtitle) : '';
  _padMsg = '';
  _padBusy = false;
  _padOnComplete = typeof onComplete === 'function' ? onComplete : null;
  _renderPad();
}

function _renderPad() {
  const content = document.getElementById('modal-content');
  const overlay = document.getElementById('modal-overlay');
  if (!content || !overlay) return;
  const msg = _padMsg ? `<p class="operator-auth-msg" role="alert">${htmlEscape(_padMsg)}</p>` : '';
  content.innerHTML = `
    <div class="modal-header">
      <h2 style="margin:0;">${htmlEscape(_padTitle)}</h2>
      <button class="modal-close" onclick="closeModal()" aria-label="Schließen">×</button>
    </div>
    <p class="hint" style="margin:.2rem 0 1rem;">4-stellige PIN eingeben — wird verschlüsselt gespeichert, nie im Klartext.</p>
    ${msg}
    ${_pinKeypadHtml({ buffer: _padBuffer, whoHtml: _padSubtitleHtml, keyFn: 'operatorPadKey', clearFn: 'operatorPadClear', delFn: 'operatorPadDel' })}
  `;
  overlay.classList.add('active');
}

export function operatorPadKey(digit) {
  if (_padBusy) return;
  if (!/^[0-9]$/.test(String(digit))) return;
  if (_padBuffer.length >= PIN_LENGTH) return;
  _padBuffer += String(digit);
  _renderPad();
  if (_padBuffer.length === PIN_LENGTH) _padSubmit();
}

export function operatorPadClear() { _padBuffer = ''; _renderPad(); }
export function operatorPadDel() { _padBuffer = _padBuffer.slice(0, -1); _renderPad(); }

async function _padSubmit() {
  if (_padBusy || !_padOnComplete) return;
  _padBusy = true;
  _padMsg = 'Speichere…';
  _renderPad();
  let res;
  try { res = await _padOnComplete(_padBuffer); }
  catch (e) { res = { ok: false, message: (e && e.message) || 'Fehler' }; }
  _padBusy = false;
  if (res && res.ok) {
    closeModal();
    _padOnComplete = null;
    return;
  }
  _padBuffer = '';
  _padMsg = (res && res.message) || 'Speichern fehlgeschlagen.';
  _renderPad();
}

/* -------------------------------------------------------------------------- */
/* Geräte-Onboarding                                                           */
/* -------------------------------------------------------------------------- */

let _onboardingLabel = '';

export function openDeviceOnboarding() {
  _onboardingLabel = '';
  _renderOnboarding();
}

function _renderOnboarding() {
  const content = document.getElementById('modal-content');
  const overlay = document.getElementById('modal-overlay');
  if (!content || !overlay) return;
  // S222 Cut 2 — Gerät = nur noch Identifikation (Name). Der Sperr-Timeout wird
  // pro Rolle im Team-Tab gesteuert → kein Timeout-Select mehr im Onboarding.
  // S224 CUT B — Pflicht-Modal: KEIN ×, und ESC darf NICHT schließen
  // (data-modal-no-esc). Ohne registriertes Gerät gäbe es sonst keinen Weg
  // zurück außer Reload. Der globale ESC-Close (dom.js) respektiert das Attribut.
  content.innerHTML = `
    <div class="modal-header" data-modal-no-esc="1">
      <h2 style="margin:0;">Gerät benennen</h2>
    </div>
    <p class="hint" style="margin:.2rem 0 1rem;">
      Dieses Gerät ist noch nicht registriert. Gib ihm einen Namen (z. B. „Theke",
      „Küche", „Kasse 1"). Die automatische Sperre bei Inaktivität wird pro Rolle
      im Team-Tab gesteuert.
    </p>
    <div class="field" style="margin-bottom:16px;">
      <label style="font-size:.85rem;">Geräte-Name</label>
      <input type="text" id="operator-onboarding-label" value="${htmlEscape(_onboardingLabel)}"
             oninput="operatorOnboardingField('label', this.value)" placeholder="Theke" maxlength="40">
    </div>
    <button class="btn btn-primary" onclick="operatorSubmitOnboarding()" style="width:100%;">Gerät registrieren</button>
  `;
  overlay.classList.add('active');
}

export function operatorOnboardingField(key, value) {
  if (key === 'label') _onboardingLabel = value;
}

export async function operatorSubmitOnboarding() {
  const label = (_onboardingLabel || '').trim();
  if (!label) { showToast('Bitte einen Geräte-Namen angeben.', 'error'); return; }
  // S222 Cut 2 — Timeout ab jetzt rollenbasiert; das Gerät wird ohne Timeout
  // registriert (RPC-Signatur bleibt, Spalte bleibt — kein DB-Touch).
  const res = await registerDevice(label, 0);
  if (!res || !res.ok) {
    const msg = res && res.error && (res.error.message || String(res.error));
    showToast('Gerät konnte nicht registriert werden: ' + (msg || 'Unbekannter Fehler'), 'error');
    return;
  }
  closeModal();
  subscribeOperatorRealtime();
  _syncIdleWatch();
  refreshOperatorCardDom();
  showToast(`Gerät „${label}" registriert.`, 'success');
  // Direkt zum Operator-Login (es ist noch niemand angemeldet).
  openOperatorSwitch();
}

/* -------------------------------------------------------------------------- */
/* Idle-Watch (Sperr-Overlay bei Inaktivität)                                  */
/* -------------------------------------------------------------------------- */

function _markActivity() { _lastActivity = Date.now(); }

/** S222 Cut 2 — Idle-Timeout des AKTUELL angemeldeten Operators in ms (0 = aus).
 *  Quelle ist die ROLLE des Operators + `settings.operatorRoleTimeouts` (pro Rolle
 *  im Team-Tab), NICHT mehr das Gerät. core ist view-frei → Settings über den
 *  window.App-Lese-Pfad ziehen (gleiches Muster wie getSupa im core). */
function _currentSettings() {
  try {
    const st = window.App && window.App.storage && typeof window.App.storage._getInternalState === 'function'
      ? window.App.storage._getInternalState() : null;
    return (st && st.settings) || {};
  } catch (_) { return {}; }
}

function _currentTimeoutMs() {
  try {
    const op = getOperator();
    if (!op || !op.role) return 0;
    const mins = roleIdleTimeoutMinutes(op.role, _currentSettings().operatorRoleTimeouts);
    return (Number.isFinite(mins) && mins > 0) ? mins * 60 * 1000 : 0;
  } catch (_) { return 0; }
}

/** S222 Cut 2 — window-Bridge: der Team-Tab ruft dies nach einer Timeout-Änderung,
 *  damit ein neuer Wert der aktuell angemeldeten Rolle sofort greift (re-sync). */
export function resyncIdleWatch() { _syncIdleWatch(); }

function _bindActivityListeners() {
  if (_activityBound || typeof document === 'undefined') return;
  _activityBound = true;
  let throttle = 0;
  const onAct = () => {
    const now = Date.now();
    if (now - throttle < 1000) return;
    throttle = now;
    _lastActivity = now;
    // S140 — dieselbe Quelle stempelt auch die persistente MainUser-Aktivität
    // (kein zweiter Listener — Memory gg-module-extract).
    markMainActivity(now);
  };
  document.addEventListener('pointerdown', onAct, { passive: true });
  document.addEventListener('keydown', onAct, { passive: true });
}

/** Startet/stoppt den Idle-Timer abhängig von Operator + Geräte-Timeout. */
function _syncIdleWatch() {
  const shouldRun = isOperatorActive() && _currentTimeoutMs() > 0;
  if (shouldRun && !_idleInterval) {
    _markActivity();
    _idleInterval = setInterval(_idleTick, IDLE_CHECK_MS);
  } else if (!shouldRun && _idleInterval) {
    clearInterval(_idleInterval);
    _idleInterval = null;
  }
}

function _idleTick() {
  if (_locked) return;
  const timeout = _currentTimeoutMs();
  if (!isOperatorActive() || timeout <= 0) { _syncIdleWatch(); return; }
  if (Date.now() - _lastActivity < timeout) return;
  // Idle erreicht → Operator abmelden + sperren.
  _locked = true;
  operatorLogout('idle').finally(() => {
    showLockOverlay();
    _syncIdleWatch();
  });
}

/* Test-Hooks (pure-nahe Logik isoliert testbar). */
export function _idleShouldLock(nowMs, lastActivityMs, timeoutMs) {
  if (!(timeoutMs > 0)) return false;
  return (nowMs - lastActivityMs) >= timeoutMs;
}

/* S222-HF1 — Test-Hooks für den Operator-Wechsel-Aktivitäts-Stempel. */
export function _handleOperatorChangedForTest() { _onOperatorChanged(); }
export function _setLastActivityForTest(ms) { _lastActivity = ms; }
export function _getLastActivityForTest() { return _lastActivity; }

export function _resetForTest() {
  _initialized = false; _teamUsers = []; _teamLoaded = false; _authContext = null;
  _selectedUserId = null; _selectedUserName = ''; _pinBuffer = ''; _authMessage = '';
  _busy = false; _locked = false; _lastSeenOperatorSessionId = null; _lastActivity = 0;
  if (_idleInterval) { clearInterval(_idleInterval); _idleInterval = null; }
}

/* -------------------------------------------------------------------------- */
/* Side-Effect: window.* für Inline-Handler (CSP via dom-bindings)             */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    operatorOpenSwitch: openOperatorSwitch,
    operatorPickUser,
    operatorPinInput,
    operatorPinClear,
    operatorPinDel,
    operatorPinBack,
    operatorLogoutNow,
    operatorOnboardingField,
    operatorSubmitOnboarding,
    operatorReopenOnboarding: openDeviceOnboarding,
    operatorPadKey,
    operatorPadClear,
    operatorPadDel,
    // S222 Cut 2 — Team-Tab ruft dies nach einer Rollen-Timeout-Änderung.
    operatorResyncIdleWatch: resyncIdleWatch
  });
}
